import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const ReturnRateOverTimeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .reduce<Array<{ date: string; returnRate: number }>>((acc, [date, day]) => {
                const metrics = (day as Record<string, unknown>).metrics as Record<string, number> | undefined;
                const grossSales = metrics?.grossSales ?? 0;
                const returns = metrics?.returns ?? 0;
                if (grossSales > 0) {
                    acc.push({
                        date,
                        returnRate: (returns / grossSales) * 100,
                    });
                }
                return acc;
            }, []);
    }, [finance?.dailyBreakdown]);

    const avgRate = useMemo(() => {
        if (chartData.length === 0) return 0;
        return chartData.reduce((sum, d) => sum + d.returnRate, 0) / chartData.length;
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: avgRate > 5 ? CHART_COLORS.danger : CHART_COLORS.revenue }}>
                {avgRate.toFixed(1)}%
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number) => [
                                `${value.toFixed(2)}%`,
                                t('charts.returnRate', 'Return Rate'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <ReferenceLine
                            y={5}
                            stroke={CHART_COLORS.warning}
                            strokeDasharray="4 4"
                            label={{
                                value: '5%',
                                position: 'right',
                                fill: CHART_COLORS.warning,
                                fontSize: 10,
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="returnRate"
                            name={t('charts.returnRate', 'Return Rate')}
                            stroke={CHART_COLORS.danger}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.danger }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
